import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { app } from "../services";
import { uploadDocumentFile } from "../services/documents";
import { extractDocumentText } from "../services/browserOcr";
import {
  detectDocumentType,
  extractStructuredData,
  summarizeExtractedData,
} from "../services/documentClassifier";
import { suggestAssignment } from "../services/documentFieldMapper";
import DocumentRecognitionModal from "./DocumentRecognitionModal";

const STATUS_LABELS = {
  pendiente: "Pendiente",
  procesando: "Procesando OCR",
  requiere_confirmacion: "Requiere confirmación",
  guardado: "Guardado",
  error: "Error",
  omitido: "Omitido",
};

function normalizeRows(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res)) return res;
  return [];
}

function isNumericLike(type = "") {
  const normalized = String(type || "").toLowerCase();
  return (
    normalized.includes("numer") ||
    normalized.includes("money") ||
    normalized.includes("moneda") ||
    normalized.includes("decimal")
  );
}

function parseToNumeric(value) {
  if (value === null || value === undefined || `${value}`.trim() === "") return null;
  const normalized = String(value)
    .replace(/\s|\$/g, "")
    .replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildInitialCurrentValues(schema = {}) {
  const map = {};
  const walk = (processes = []) => {
    (processes || []).forEach((process) => {
      (process?.fields || []).forEach((field) => {
        const value = field?.value || {};
        if (value?.id_documento) map[field.id] = value.id_documento;
        else if (value?.vc_valor !== undefined && value?.vc_valor !== null && `${value.vc_valor}`.trim() !== "") {
          map[field.id] = value.vc_valor;
        } else if (value?.nu_valor !== undefined && value?.nu_valor !== null && `${value.nu_valor}`.trim() !== "") {
          map[field.id] = value.nu_valor;
        } else if (value?.tx_valor !== undefined && value?.tx_valor !== null && `${value.tx_valor}`.trim() !== "") {
          map[field.id] = value.tx_valor;
        }
      });
      if (Array.isArray(process?.sub_processes) && process.sub_processes.length) {
        walk(process.sub_processes);
      }
    });
  };
  walk(schema?.processes || []);
  return map;
}

function humanFileSize(bytes) {
  const units = ["B", "KB", "MB", "GB"];
  let b = Number(bytes || 0);
  let i = 0;
  while (b >= 1024 && i < units.length - 1) {
    b /= 1024;
    i += 1;
  }
  return `${b.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function docStatusColor(status) {
  if (status === "guardado") return "success";
  if (status === "procesando" || status === "requiere_confirmacion") return "info";
  if (status === "error") return "error";
  if (status === "omitido") return "default";
  return "warning";
}

export default function SubirDocumentosInteligente({
  id_cliente,
  id_institucion,
  id_usuario,
  id_product_assignment,
  schema,
  currentValues,
  disabled,
  onSaved,
  onError,
  onProgress,
  buttonText = "Subir Documentos",
  buttonVariant = "outlined",
  buttonColor = "primary",
}) {
  const inputRef = React.useRef(null);
  const [queue, setQueue] = React.useState([]);
  const [processing, setProcessing] = React.useState(false);
  const [localError, setLocalError] = React.useState("");
  const [reviewItem, setReviewItem] = React.useState(null);
  const [reviewDocType, setReviewDocType] = React.useState("otro");
  const [reviewExtractedData, setReviewExtractedData] = React.useState({});
  const [reviewDocFieldId, setReviewDocFieldId] = React.useState("");
  const [reviewDocFieldOptions, setReviewDocFieldOptions] = React.useState([]);
  const [savingReview, setSavingReview] = React.useState(false);
  const [resolver, setResolver] = React.useState(null);

  const workingValuesRef = React.useRef({});
  const currentProcessingIdRef = React.useRef("");

  React.useEffect(() => {
    workingValuesRef.current = {
      ...buildInitialCurrentValues(schema || {}),
      ...(currentValues || {}),
    };
  }, [schema, currentValues]);

  const canUpload = !!id_cliente && !!id_institucion;

  const openFileManager = () => {
    if (!canUpload) {
      const message = "Primero guarda el cliente para vincular documentos y datos.";
      setLocalError(message);
      onError?.(new Error(message));
      return;
    }
    setLocalError("");
    inputRef.current?.click();
  };

  const updateQueueItem = React.useCallback((id, patch) => {
    setQueue((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const findExistingClienteData = async (idField) => {
    if (!idField || !id_cliente || !id_institucion) return null;
    const response = await app.service("clientes-data").find({
      query: {
        id_institucion,
        id_cliente,
        id_field: idField,
        ...(id_product_assignment ? { id_product_assignment } : {}),
        $limit: 1,
        $sort: { updatedAt: -1 },
      },
    });
    return normalizeRows(response)[0] || null;
  };

  const upsertClienteData = async ({ field, value, idDocumento = null, semanticKey = "" }) => {
    if (!field?.id) return null;

    const existing = await findExistingClienteData(field.id);
    const payload = {
      id_institucion,
      id_cliente,
      id_product_assignment: id_product_assignment || null,
      id_proceso: field?._process_id || field?.id_proceso || null,
      id_field: field.id,
      id_usuario: id_usuario || null,
      id_documento: idDocumento || existing?.id_documento || null,
      vc_origen: "ocr_front",
      vc_valor: null,
      nu_valor: null,
    };

    const numericPreferred = semanticKey === "ingresosMensuales" || isNumericLike(field?.vc_tipo);
    if (numericPreferred) {
      payload.nu_valor = parseToNumeric(value);
      payload.vc_valor = payload.nu_valor === null ? String(value || "") : null;
    } else {
      payload.vc_valor = value === null || value === undefined ? "" : String(value);
    }

    if (existing?.id && !String(existing.id).startsWith("virtual_")) {
      return app.service("clientes-data").patch(existing.id, payload);
    }
    return app.service("clientes-data").create(payload);
  };

  const requestReview = (payload) =>
    new Promise((resolve) => {
      setResolver(() => resolve);
      setReviewItem(payload.item);
      setReviewDocType(payload.docType || "otro");
      setReviewExtractedData(payload.extractedData || {});
      setReviewDocFieldOptions(payload.documentFieldOptions || []);
      setReviewDocFieldId(payload.suggestedDocumentField?.id || "");
    });

  const closeReview = (decision) => {
    if (resolver) resolver(decision);
    setResolver(null);
    setReviewItem(null);
    setReviewDocType("otro");
    setReviewExtractedData({});
    setReviewDocFieldId("");
    setReviewDocFieldOptions([]);
  };

  const processSingleFile = async (item) => {
    currentProcessingIdRef.current = item.id;
    updateQueueItem(item.id, { status: "procesando", error: "" });
    onProgress?.({ id: item.id, status: "procesando", file: item.file });

    const ocr = await extractDocumentText(item.file);
    const detected = detectDocumentType(ocr.text, {
      name: item.file?.name,
      type: item.file?.type,
    });

    const extractedData = extractStructuredData(ocr.text, detected.type);
    const assignment = suggestAssignment(
      schema,
      detected.type,
      extractedData,
      workingValuesRef.current,
    );

    updateQueueItem(item.id, {
      status: "requiere_confirmacion",
      detectedType: detected.type,
      confidence: detected.confidence,
      extractedKeys: summarizeExtractedData(extractedData),
      ocrWarnings: ocr.warnings || [],
    });

    const decision = await requestReview({
      item,
      docType: detected.type,
      extractedData,
      suggestedDocumentField: assignment.suggestedDocumentField,
      documentFieldOptions: assignment.documentCandidates,
      dataMappings: assignment.dataMappings,
      ocr,
      detected,
    });

    if (!decision || decision.action === "skip") {
      updateQueueItem(item.id, { status: "omitido" });
      onProgress?.({ id: item.id, status: "omitido", file: item.file });
      return;
    }

    const finalDocType = decision.docType || detected.type || "otro";
    const finalExtractedData = decision.extractedData || extractedData;
    const finalDocFieldId = decision.documentFieldId;

    const reassigned = suggestAssignment(
      schema,
      finalDocType,
      finalExtractedData,
      workingValuesRef.current,
    );

    const selectedDocumentField =
      reassigned.documentCandidates.find((field) => field.id === finalDocFieldId) ||
      reassigned.suggestedDocumentField;

    if (!selectedDocumentField?.id) {
      throw new Error("No se encontró campo documental compatible para guardar.");
    }

    setSavingReview(true);
    const document = await uploadDocumentFile({
      file: item.file,
      id_institucion,
      id_owner: id_usuario,
      id_cliente,
      vc_nombre_original: item.file?.name,
    });

    await upsertClienteData({
      field: selectedDocumentField,
      value: item.file?.name || selectedDocumentField?.vc_label || "Documento",
      idDocumento: document?.id || null,
      semanticKey: "document",
    });

    const finalDataMappings = suggestAssignment(
      schema,
      finalDocType,
      finalExtractedData,
      workingValuesRef.current,
    ).dataMappings;

    for (const mapping of finalDataMappings) {
      await upsertClienteData({
        field: mapping.field,
        value: finalExtractedData[mapping.semanticKey],
        idDocumento: null,
        semanticKey: mapping.semanticKey,
      });
      workingValuesRef.current[mapping.field.id] = finalExtractedData[mapping.semanticKey];
    }

    workingValuesRef.current[selectedDocumentField.id] = document?.id || item.file?.name;

    updateQueueItem(item.id, {
      status: "guardado",
      documentId: document?.id || null,
      detectedType: finalDocType,
      extractedKeys: summarizeExtractedData(finalExtractedData),
      assignedDocumentField: selectedDocumentField?.vc_label || selectedDocumentField?.vc_nombre || selectedDocumentField?.id,
    });

    onSaved?.({
      item,
      document,
      docType: finalDocType,
      selectedDocumentField,
      extractedData: finalExtractedData,
      dataMappings: finalDataMappings,
    });

    onProgress?.({
      id: item.id,
      status: "guardado",
      file: item.file,
      documentId: document?.id || null,
    });
    currentProcessingIdRef.current = "";
  };

  const processFilesSequentially = async (items) => {
    setProcessing(true);
    setLocalError("");
    try {
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        await processSingleFile(item);
      }
    } catch (error) {
      const message = error?.message || "No fue posible procesar uno de los documentos.";
      setLocalError(message);
      onError?.(error);
      const currentId = currentProcessingIdRef.current || "";
      if (currentId) {
        updateQueueItem(currentId, { status: "error", error: message });
      }
    } finally {
      currentProcessingIdRef.current = "";
      setSavingReview(false);
      setProcessing(false);
    }
  };

  const handleFilesSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    if (!files.length) return;

    const created = files.map((file) => ({
      id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      file,
      status: "pendiente",
      error: "",
      extractedKeys: [],
      detectedType: "",
      documentId: null,
    }));

    setQueue((prev) => [...prev, ...created]);
    await processFilesSequentially(created);
  };

  React.useEffect(() => {
    if (!reviewItem) return;
    const nextOptions = suggestAssignment(
      schema,
      reviewDocType,
      reviewExtractedData,
      workingValuesRef.current,
    ).documentCandidates;
    setReviewDocFieldOptions(nextOptions);
    if (!nextOptions.some((field) => field.id === reviewDocFieldId)) {
      setReviewDocFieldId(nextOptions[0]?.id || "");
    }
  }, [reviewDocType, reviewExtractedData, schema, reviewItem]);

  const completed = queue.filter((item) => item.status === "guardado").length;
  const total = queue.length;

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple
        accept="image/*,.pdf,application/pdf,application/octet-stream"
        onChange={handleFilesSelected}
      />

      <Stack spacing={1}>
        <Button
          variant={buttonVariant}
          color={buttonColor}
          startIcon={<UploadFileIcon />}
          onClick={openFileManager}
          disabled={disabled || processing}
        >
          {buttonText}
        </Button>

        {processing && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Procesando documentos ({completed}/{total})
            </Typography>
          </Stack>
        )}

        {!!localError && <Alert severity="error">{localError}</Alert>}

        {queue.length > 0 && (
          <Stack spacing={0.75}>
            {queue.map((item) => (
              <Box
                key={item.id}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2">{item.file?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {humanFileSize(item.file?.size || 0)}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={docStatusColor(item.status)}
                    label={STATUS_LABELS[item.status] || item.status}
                  />
                </Stack>
                {!!item.detectedType && (
                  <Typography variant="caption" color="text.secondary">
                    Tipo: {item.detectedType}
                  </Typography>
                )}
                {!!item.error && (
                  <Typography variant="caption" color="error.main" display="block">
                    {item.error}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Stack>

      <DocumentRecognitionModal
        open={!!reviewItem}
        item={reviewItem}
        documentType={reviewDocType}
        onChangeDocumentType={(value) => setReviewDocType(value)}
        selectedDocumentFieldId={reviewDocFieldId}
        onChangeDocumentField={(value) => setReviewDocFieldId(value)}
        documentFieldOptions={reviewDocFieldOptions}
        extractedData={reviewExtractedData}
        onChangeExtractedData={(key, value) =>
          setReviewExtractedData((prev) => ({ ...prev, [key]: value }))
        }
        processing={savingReview}
        onCancel={() => closeReview({ action: "skip" })}
        onConfirm={() =>
          closeReview({
            action: "confirm",
            docType: reviewDocType,
            documentFieldId: reviewDocFieldId,
            extractedData: reviewExtractedData,
          })
        }
      />
    </Box>
  );
}
